import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminGrowthSnapshotOverallReflectionCreateAction,
  adminGrowthSnapshotOverallReflectionGetAction,
  adminGrowthSnapshotOverallReflectionUpdateAction,
  adminGrowthSnapshotsListAction
} from "../Action/action";

const PROFILES = [
  { value: "flourishing_journey", label: "Flourishing journey" },
  { value: "steady_growth", label: "Steady growth" },
  { value: "mixed_journey", label: "Mixed journey" },
  { value: "holding_steady", label: "Holding steady" },
  { value: "gentle_focus", label: "Gentle focus" }
];

export default function OverallReflectionForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { reflectionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    growth_snapshot_id: searchParams.get("snapshot_id") || "",
    internal_profile: "flourishing_journey",
    heading: "Overall Reflection",
    body_text: "",
    status: 1
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotsListAction({ page: 1, per_page: 100 });
        if (!cancelled) setSnapshots(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit || !reflectionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminGrowthSnapshotOverallReflectionGetAction(reflectionId);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          toast.error(res?.message || "Not found", { toastId: "gs-ov-form" });
          navigate("/reflections-screens/overall-reflections");
          return;
        }
        const d = res.data;
        setForm({
          growth_snapshot_id: String(d.growth_snapshot_id || ""),
          internal_profile: d.internal_profile || "flourishing_journey",
          heading: d.heading || "Overall Reflection",
          body_text: d.body_text || "",
          status: Number(d.status) === 0 ? 0 : 1
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to load", {
          toastId: "gs-ov-form"
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, reflectionId, navigate]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const listPath = form.growth_snapshot_id
    ? `/reflections-screens/overall-reflections?snapshot_id=${form.growth_snapshot_id}`
    : "/reflections-screens/overall-reflections";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.growth_snapshot_id) {
      toast.error("Select a growth snapshot", { toastId: "gs-ov-form" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        internal_profile: form.internal_profile,
        heading: form.heading.trim() || "Overall Reflection",
        body_text: form.body_text || "",
        status: Number(form.status) === 1 ? 1 : 0
      };
      const res = isEdit
        ? await adminGrowthSnapshotOverallReflectionUpdateAction(reflectionId, payload)
        : await adminGrowthSnapshotOverallReflectionCreateAction(form.growth_snapshot_id, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Updated" : "Created", { toastId: "gs-ov-form" });
      navigate(listPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "gs-ov-form"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/reflections-screens">Reflections & screens</Link>
          <span>›</span>
          <Link to={listPath}>Overall reflections</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"}</span>
        </div>
        <Link className="btn btn-outline btn-sm" to={listPath}>
          ← Back
        </Link>
      </div>

      <form onSubmit={onSubmit}>
        <div className="card mb-6">
          <h2 className="section-title">{isEdit ? "Edit overall reflection" : "Add overall reflection"}</h2>
          <p className="small-label" style={{ marginBottom: 16 }}>
            Internal profile is never shown to parents — only heading and body text.
          </p>

          <div className="form-group">
            <label>
              Growth snapshot <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={form.growth_snapshot_id}
              disabled={isEdit}
              onChange={(e) => setField("growth_snapshot_id", e.target.value)}
              required
            >
              <option value="">Select…</option>
              {snapshots.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.title || `Snapshot #${s.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Internal profile</label>
              <select
                className="form-control"
                value={form.internal_profile}
                disabled={isEdit}
                onChange={(e) => setField("internal_profile", e.target.value)}
              >
                {PROFILES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Heading</label>
              <input
                className="form-control"
                value={form.heading}
                onChange={(e) => setField("heading", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setField("status", Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Body text</label>
            <textarea
              className="form-control"
              rows={6}
              value={form.body_text}
              onChange={(e) => setField("body_text", e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to={listPath}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
